<template>
  <section class="mt-10">
    <h2 class="text-lg font-bold tracking-tight text-gray-900">Course content</h2>
    <p class="mt-0.5 text-sm text-gray-500">
      {{ course.course_sections.length }} modules · {{ completedLectures }} of {{ totalLectures }} lessons complete
    </p>

    <Accordion
      type="multiple"
      :default-value="defaultOpen"
      class="mt-5 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm"
    >
      <AccordionItem
        v-for="(section, sectionIndex) in course.course_sections"
        :key="section.id"
        :value="`section-${section.id}`"
        class="border-gray-200 px-5"
      >
        <AccordionTrigger class="hover:no-underline">
          <div class="flex min-w-0 flex-1 items-start gap-3 pr-2">
            <span
              class="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold"
              :class="sectionComplete(section) ? 'bg-green-100 text-green-700' : 'bg-[#F4F3E3] text-[#4D2542]'"
            >
              <vue-feather v-if="sectionComplete(section)" type="check" size="13" />
              <template v-else>{{ sectionIndex + 1 }}</template>
            </span>
            <div class="min-w-0">
              <p class="font-semibold leading-snug text-gray-900">{{ section.name }}</p>
              <p class="mt-0.5 text-xs font-normal text-gray-400">
                {{ completedInSection(section) }} / {{ section.lectures.length }} lessons
              </p>
            </div>
          </div>
        </AccordionTrigger>

        <AccordionContent class="pb-2">
          <p v-if="section.description" class="mb-3 ml-9 text-sm leading-relaxed text-gray-500">
            {{ section.description }}
          </p>
          <ul class="flex flex-col">
            <li v-for="lecture in section.lectures" :key="lecture.id">
              <RouterLink
                :to="{ name: 'University Lecture', params: { course_slug: course.slug, order: lecture.order } }"
                class="group -mx-2 flex items-center gap-3 rounded-lg px-2 py-2.5 transition-colors hover:bg-gray-50"
              >
                <vue-feather
                  :type="lecture.completed ? 'check-circle' : 'play-circle'"
                  size="18"
                  class="shrink-0 transition-colors"
                  :class="lecture.completed ? 'text-green-500' : 'text-gray-300 group-hover:text-[#202F7E]'"
                />
                <span class="min-w-0 flex-1 text-sm leading-snug text-gray-700 group-hover:text-gray-900">
                  {{ lecture.name }}
                </span>
                <vue-feather
                  type="chevron-right"
                  size="16"
                  class="shrink-0 text-gray-300 transition-all group-hover:translate-x-0.5 group-hover:text-gray-500"
                />
              </RouterLink>
            </li>
          </ul>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  </section>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import type { CourseWithSections, SectionWithLectures } from '@/api/agent/university/course';

const props = defineProps<{ course: CourseWithSections }>();

const totalLectures = computed(() =>
  props.course.course_sections.reduce((sum, section) => sum + section.lectures.length, 0)
);

const completedLectures = computed(() =>
  props.course.course_sections.reduce(
    (sum, section) => sum + section.lectures.filter(lecture => lecture.completed).length,
    0
  )
);

const completedInSection = (section: SectionWithLectures) =>
  section.lectures.filter(lecture => lecture.completed).length;

const sectionComplete = (section: SectionWithLectures) =>
  section.lectures.length > 0 && section.lectures.every(lecture => lecture.completed);

const defaultOpen = computed(() => {
  const section =
    props.course.course_sections.find(s => s.lectures.some(lecture => !lecture.completed)) ??
    props.course.course_sections[0];
  return section ? [`section-${section.id}`] : [];
});
</script>
